import { useEffect } from "react";
import type { AppProps } from "next/app";
import Script from "next/script";
import "@/public/css/fonts.css";
import "@/public/css/style.css";
import "keen-slider/keen-slider.min.css";
import mixpanel from "mixpanel-browser";
import Meta from "@/components/meta";
import { useRouter } from "next/router";
import * as gtag from "../lib/gtag";
import { I18nProvider } from "next-localization";

let didInitMixpanel = false;
const track = (
  feature: string,
  propertyName: string,
  eventType: "click" | "view",
  extraProperties = {}
) => {
  const isProd = "watershed.com" === window.location.hostname;
  if (!didInitMixpanel) {
    mixpanel.init(
      isProd
        ? "ea14cd223be763ce1b8b6894b0a8e725"
        : "8c7027f56f03bd885ed39ecf7de8ef28"
    );
    didInitMixpanel = true;
  }

  const domain = "marketing";
  const eventKey = `${domain}.${feature}.${propertyName}.${eventType}`;
  const properties = {
    domain,
    feature,
    propertyName,
    eventType,

    isCustomer: isProd,
    isLoginAsUser: false,

    path: window.location.pathname,
    genericPath: window.location.pathname,
    queryParams: window.location.search,
    selectedText: window.getSelection().toString(),

    ...extraProperties,
  };
  mixpanel.track(eventKey, properties);
};

function globalClickHandler(evt: MouseEvent) {
  const target = evt.target as HTMLElement;

  // Log analytics.
  {
    let currentTarget = target;
    while (
      currentTarget !== null &&
      !currentTarget.getAttribute("data-track-id") &&
      currentTarget.tagName !== "A"
    ) {
      currentTarget = currentTarget.parentElement;
    }

    let featureAndPropertyName: [string, string] = ["body", "catchAll"];
    if (currentTarget) {
      if (currentTarget.getAttribute("data-track-id")) {
        featureAndPropertyName = currentTarget
          .getAttribute("data-track-id")
          .split(".") as [string, string];
      } else if (currentTarget.tagName === "A") {
        featureAndPropertyName = [
          "link",
          (currentTarget as HTMLLinkElement).href,
        ];
      }
    }
    const [feature, propertyName] = featureAndPropertyName;
    track(feature, propertyName, "click");
  }
}

function App({ Component, pageProps }: AppProps) {
  useEffect(() => {
    document.addEventListener("click", globalClickHandler);
  }, []);

  useEffect(() => {
    const pageName = document.location.pathname.substr(1) || "home";
    track(pageName, pageName, "view");
  });

  const router = useRouter();
  useEffect(() => {
    const handleRouteChange = (url) => {
      gtag.pageview(url);
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);
  const { localeCopy, ...componentProps } = pageProps;

  return (
    <>
      <Script
        id="gtag"
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${gtag.GA_MEASUREMENT_ID}`}
      />
      <Script
        id="gtag-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${gtag.GA_MEASUREMENT_ID}');
            gtag('config', '${gtag.UA_TRACKING_ID}');
          `,
        }}
      />
      <Script
        id="gtm-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtag.GTM_TRACKING_ID}');
`,
        }}
      />
      <Meta />
      <I18nProvider lngDict={localeCopy} locale={router.locale}>
        {/* @ts-expect-error React 18 issue */}
        <Component {...componentProps} />
      </I18nProvider>
    </>
  );
}

export default App;
